import { useEffect, useRef, useState } from 'react';

export default function OregonDogRescueSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const missionPillars = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'No-Kill Shelter',
      description: '100% commitment to saving every adoptable dog'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Foster Network',
      description: 'Home-based care in loving foster families'
    },
    {
      icon: 'ri-stethoscope-line',
      title: 'Full Vet Care',
      description: 'Complete medical care, spay/neuter, vaccinations'
    },
    {
      icon: 'ri-team-line',
      title: 'Community Supported',
      description: 'Funded entirely by donations and adoption fees'
    }
  ];

  const stats = [
    { label: 'Founded', value: '2007' },
    { label: 'Status', value: '501(c)(3) Non-Profit' },
    { label: 'Location', value: 'Portland, OR Area' },
    { label: 'Model', value: 'Foster-Based' }
  ];

  return (
    <section
      ref={sectionRef}
      className={`py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-heart-fill"></i>
            <span>Our Charity Partner</span>
          </div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-3xl sm:text-4xl font-bold text-[#1A2332] mb-4">
            Oregon Dog Rescue
          </h2>
          <p className="text-base sm:text-xl text-[#4A5568] max-w-3xl mx-auto">
            A no-kill shelter dedicated to rescuing, rehabilitating, and rehoming dogs in need across the Portland metro area since 2007
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-4 sm:p-6 text-center shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-1">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-[#4A5568] font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Mission Pillars */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 mb-8">
          <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-2xl font-bold text-[#1A2332] mb-6 text-center">
            Their Mission &amp; Values
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {missionPillars.map((pillar, index) => (
              <div
                key={index}
                className="text-center p-4 sm:p-6 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <div className="w-12 h-12 mx-auto mb-3 bg-amber-600 rounded-full flex items-center justify-center">
                  <i className={`${pillar.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="font-bold text-[#1A2332] mb-1 text-sm sm:text-base">{pillar.title}</h4>
                <p className="text-xs sm:text-sm text-[#4A5568]">{pillar.description}</p>
              </div>
            ))}
          </div>

          {/* Mission Statement */}
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-5 border-l-4 border-amber-600">
            <p className="text-[#4A5568] leading-relaxed text-sm sm:text-base">
              <strong className="text-amber-900">Oregon Dog Rescue</strong> is a 501(c)(3) non-profit, no-kill shelter operating entirely through a foster-based model. Every dog receives complete veterinary care, behavioral assessment, and lives in a loving home environment while awaiting adoption. With no government funding, they rely 100% on community support through donations and adoption fees to continue their life-saving work.
            </p>
          </div>
        </div>

        {/* Partnership Story */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E8B84B] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-hand-heart-line text-xl sm:text-2xl text-white"></i>
            </div>
            <div>
              <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-xl sm:text-2xl font-bold text-[#1A2332] mb-2">
                How Rockhound Supports Oregon Dog Rescue
              </h3>
              <p className="text-[#4A5568] leading-relaxed text-sm sm:text-base">
                For every vehicle we sell, Rockhound Auto Brokers donates <strong className="text-amber-900">$100 directly to Oregon Dog Rescue</strong>. This partnership means that when you buy a car through us, you're not just getting a great deal — you're helping save dogs' lives. Since launching this program, we've contributed thousands of dollars to support their foster network, veterinary care, and adoption programs.
              </p>
            </div>
          </div>

          <div className="bg-[#E8B84B]/10 rounded-xl p-5 border-l-4 border-[#E8B84B]">
            <p className="text-[#4A5568] italic text-sm sm:text-base">
              "Every car sold = one more dog rescued. It's that simple. We're proud to partner with Oregon Dog Rescue and grateful to our customers who make this impact possible."
            </p>
            <p className="text-sm text-[#4A5568] mt-2 font-semibold">
              — Rockhound Auto Brokers Team
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://www.oregondogrescue.org/donate"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-4 rounded-lg font-bold text-base sm:text-lg hover:from-amber-700 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className="ri-heart-fill text-xl"></i>
            <span>Donate to Oregon Dog Rescue</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
          <a
            href="https://www.oregondogrescue.org"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-amber-700 px-8 py-4 rounded-lg font-bold text-base sm:text-lg border-2 border-amber-600 hover:bg-amber-50 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
          >
            <i className="ri-external-link-line text-xl"></i>
            <span>Visit oregondogrescue.org</span>
          </a>
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-8">
          <p className="text-sm text-[#4A5568]">
            Oregon Dog Rescue is a registered 501(c)(3) non-profit organization. All donations are tax-deductible.
          </p>
        </div>
      </div>
    </section>
  );
}
